import { createContext, useContext, useMemo } from "react";
import { mediaResolverFor } from "../utils/mediaResolver";

/**
 * The media host for the page's deployment, shared with whatever it renders.
 *
 * A prop would have done, except the modals are not a flat list: the case page
 * renders the parts modal, which itself renders the edit modal and a second
 * lightbox. Threading meant one component accepting a resolver purely to
 * forward it, and any child added later silently reverting to the session
 * host — which is precisely how this bug reached five files.
 */
const MediaUrlContext = createContext(null);

/** Publishes the resolver for the deployment this page is showing. */
export function MediaUrlProvider({ deployment, children }) {
  // `deployment` is a frozen module constant or undefined, so this identity
  // is stable and safe to put in a dependency array.
  const resolve = useMemo(() => mediaResolverFor(deployment), [deployment]);
  return (
    <MediaUrlContext.Provider value={resolve}>
      {children}
    </MediaUrlContext.Provider>
  );
}

/**
 * `(path) => url` for the surrounding page's deployment.
 *
 * Outside a provider it resolves the logged-in client — which is every screen
 * except the break-in detail page, and is exactly what they all did before.
 */
export function useMediaUrl() {
  const provided = useContext(MediaUrlContext);
  const session = useMemo(() => mediaResolverFor(null), []);
  return provided || session;
}

export default MediaUrlContext;
